// src/services/supabase.js
import { createClient } from '@supabase/supabase-js';

// CRA-style env vars
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // This will help you catch misconfigured env in dev
  console.warn('Supabase URL or anon key is missing. Check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    detectSessionInUrl: true,
  },
});

/**
 * AUTH SERVICE
 * This matches what AuthContext expects:
 * - getCurrentSession()
 * - onAuthStateChange()
 * - signInWithGoogle()
 * - signOut()
 */
export const auth = {
  async getCurrentSession() {
    // Supabase v2: getSession()
    const result = await supabase.auth.getSession();
    // AuthContext expects { data: { session } }
    return {
      data: {
        session: result.data?.session ?? null,
      },
      error: result.error,
    };
  },

  onAuthStateChange(callback) {
    // Pass through Supabase's subscription
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
    // AuthContext expects { data: { subscription } }
    return { data };
  },

  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // After Google → Supabase → back to your app
          redirectTo: `${window.location.origin}/home`,
        },
      });

      if (error) {
        return { success: false, error };
      }

      // For OAuth, Supabase will redirect the browser away,
      // so this "success" is mostly pre-redirect success.
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      return { success: false, error };
    }
    return { success: true };
  },
};

/**
 * DATABASE SERVICE
 * These are the functions AuthContext calls:
 * - upsertUserProfile(user)
 * - getUserConsent(userId)
 * - recordConsent(email)
 * - withdrawConsent(email)
 * - recordLogin(userId, email)
 *
 * Implemented directly via Supabase tables + RPC.
 */
export const database = {
  /**
   * Upsert user profile into public.user_profiles
   * Called whenever a signed-in user is detected.
   */
  async upsertUserProfile(user) {
    if (!user) {
      return { success: false, error: 'No user provided' };
    }

    const payload = {
      user_id: user.id,
      email: user.email,
      full_name:
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        user.email,
      avatar_url: user.user_metadata?.avatar_url || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) {
      console.error('upsertUserProfile error:', error);
      return { success: false, error };
    }

    return { success: true, data };
  },

  /**
   * Get current consent state for a user by canonical Supabase Auth UUID.
   * AuthContext expects: { success, hasConsent }
   */
  async getUserConsent(userId) {
    if (!userId) {
      return { success: false, hasConsent: false, error: 'No user ID provided' };
    }

    const { data, error } = await supabase
      .from('user_consent')
      .select('has_consent')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('getUserConsent error:', error);
      return { success: false, hasConsent: false, error };
    }

    if (!data) {
      // No row yet → treat as no consent
      return { success: true, hasConsent: false };
    }

    return { success: true, hasConsent: !!data.has_consent };
  },

  /**
   * Record consent = true for this user/email.
   */
  async recordConsent(email) {
    if (!email) {
      return { success: false, error: 'No email provided' };
    }

    // Get current authenticated user to get user_id
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error('recordConsent getUser error:', userError);
      return { success: false, error: userError || 'User not authenticated' };
    }

    const now = new Date().toISOString();

    const payload = {
      user_id: user.id,
      email,
      has_consent: true,
      consent_given_at: now,
      consent_withdrawn_at: null,
      updated_at: now,
    };

    const { data, error } = await supabase
      .from('user_consent')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) {
      console.error('recordConsent error:', error);
      return { success: false, error };
    }

    return { success: true, data };
  },

  /**
   * Withdraw consent = false for this user/email.
   */
  async withdrawConsent(email) {
    if (!email) {
      return { success: false, error: 'No email provided' };
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error('withdrawConsent getUser error:', userError);
      return { success: false, error: userError || 'User not authenticated' };
    }

    const now = new Date().toISOString();

    const payload = {
      user_id: user.id,
      email,
      has_consent: false,
      consent_withdrawn_at: now,
      updated_at: now,
    };

    const { data, error } = await supabase
      .from('user_consent')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) {
      console.error('withdrawConsent error:', error);
      return { success: false, error };
    }

    return { success: true, data };
  },

  /**
   * Record login via the record_login RPC you already created.
   * This tracks login_count and last_login_at.
   */
  async recordLogin(userId, email) {
    try {
      const { error } = await supabase.rpc('record_login', {
        p_user_id: userId,
        p_email: email,
        // p_login_time omitted → default NOW() in SQL
      });

      if (error) {
        console.error('record_login RPC error:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (err) {
      console.error('recordLogin unexpected error:', err);
      return { success: false, error: err };
    }
  },
};
